import React from "react";
import { Helmet } from "react-helmet";
import {
  Navigate,
  createBrowserRouter,
  RouterProvider,
  Outlet,
} from "react-router-dom";

// Pages
import AboutUs from "./pages/about-us";
import Admin from "./pages/admin";
import Cart from "./pages/cart";
import Category from "./pages/category";
import Checkout from "./pages/checkout";
import ContactPage from "./pages/contact";
import CompletePasswordChange from "./pages/auth/complete-password-change";
import ConfirmAccount from "./pages/auth/confirm-account";
import EditProfile from "./pages/auth/edit-profile";
import ForgotPassword from "./pages/auth/forgot-password";
import Home from "./pages/home";
import SignUp from "./pages/auth/signup";
import LogIn from "./pages/auth/login";
import Detail from "./pages/detail";
import ErrorPage from "./pages/error";
import SearchPage from "./pages/search/[friendlyUrl]";

// Components
import NavBar from "./components/NavBar";
import HomeImagesScroll from "./components/HomeImagesScroll";
import PrivateRoutes from "./components/PrivateRouter";

// Context
import { useAuth } from "./utils/hooks/useIsAuth";

// Styles
import "./styles/styles.scss";
import Catalog from "./pages/catalog";
import WhatsappWidget from "./components/common/WhatsappWidget";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider } from "./context/AuthContext";
import { UserContextProvider } from "./context/UserContext";
import { CartProvider } from "./context/CartContext";

// Add this to it's own context, it shouldn't be in here
export type State = {
  productName: string;
  products: Product[];
};

const Layout = () => {
  return (
    <AuthProvider>
      <UserContextProvider>
        <CartProvider>
          <div>
            <Helmet>
              <meta charSet="utf-8" />
              <title>RTABAK</title>
            </Helmet>
            <div id="App">
              <HomeImagesScroll />
              <NavBar />
              <Outlet />
              <WhatsappWidget />
            </div>
          </div>
        </CartProvider>
      </UserContextProvider>
    </AuthProvider>
  );
};

const RedirecToRoot = () => {
  const { isAuth } = useAuth();
  return isAuth ? <Navigate to="/" /> : <Outlet />;
};

const router = createBrowserRouter([
  {
    element: <Layout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: "/",
        element: <Home />,
      },
      {
        path: "/catalog/:catalog/:solid",
        element: <Catalog />,
      },
      {
        path: "/edit-profile",
        element: <EditProfile />,
      },
      {
        path: "/producto/:friendlyUrl",
        element: <Detail />,
      },
      { path: "/category/:category", element: <Category /> },
      { path: "/contact", element: <ContactPage /> },
      {
        element: <RedirecToRoot />,
        children: [
          {
            path: "/login",
            element: <LogIn />,
          },
          {
            path: "/sign-up",
            element: <SignUp />,
          },
          {
            path: "/confirm-account",
            element: <ConfirmAccount />,
          },
          {
            path: "/forgot-password",
            element: <ForgotPassword />,
          },
          {
            path: "/confirm-password",
            element: <CompletePasswordChange />,
          },
        ],
      },
      {
        element: <PrivateRoutes />,
        children: [
          {
            path: "/admin/:action",
            element: <Admin />,
          },
          {
            path: "/admin/",
            element: <Admin />,
          },
          {
            path: "/admin/edit/:productFriendlyUrl",
            element: <Admin />,
          },
          {
            path: "/cart",
            element: <Cart />,
          },
          { path: "/checkout", element: <Checkout /> },
          { path: "/about-us", element: <AboutUs /> },
          { path: "/search/:friendlyUrl", element: <SearchPage /> },
        ],
      },
      {
        path: "*",
        element: (
          <ErrorPage
            message="Lo lamentamos, parece que no podemos encontrar la página que estás
              buscando"
          />
        ),
      },
    ],
  },
]);

const App = () => {
  return <RouterProvider router={router} />;
};

export default App;
